
import { collection, doc, getDocs, query, where, getDoc, updateDoc, DocumentData } from 'firebase/firestore';
import db from 'services/firebase';

/**
 * Gets the active frameworks for a tenant
 * @param tenantId - The tenant ID
 * @returns Promise with array of active frameworks
 */
export const getActiveFrameworks = async (tenantId: string): Promise<string[]> => {
  try {
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    const tenantRef = doc(db, 'tenants', tenantId);
    const frameworksCollectionRef = collection(tenantRef, 'active_frameworks');
    const frameworksSnapshot = await getDocs(frameworksCollectionRef);
    
    if (frameworksSnapshot.empty) {
      return [];
    }
    
    const frameworksData = frameworksSnapshot.docs[0].data();
    return frameworksData.frameworks || [];
  } catch (error) {
    console.error('Error getting active frameworks:', error);
    throw error;
  }
};

/**
 * Checks if all policies in a collection have passed for a specific control
 * @param controlId - The ID of the control to check
 * @param tenantId - The tenant ID
 * @returns Promise with boolean indicating if test passes and status string
 */
export const checkPoliciesTest = async (controlId: string, tenantId: string): Promise<{
  passes: boolean;
  status: string;
}> => {
  try {
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    const tenantRef = doc(db, 'tenants', tenantId);
    const controlsCollectionRef = collection(tenantRef, 'controls');
    const controlDoc = await getDoc(doc(controlsCollectionRef, controlId));

    if (!controlDoc.exists()) {
      throw new Error(`Control with ID ${controlId} not found`);
    }

    const policiesCollectionRef = collection(tenantRef, 'policies');
    
    const policiesQuery = query(policiesCollectionRef, where('control_uid', '==', controlId));
    const policiesSnapshot = await getDocs(policiesQuery);
    
    let totalPolicies = 0;
    let passedPolicies = 0;
    
    policiesSnapshot.forEach((doc) => {
      totalPolicies++;
      const policyData = doc.data();
      
      if (
        policyData.status === 'OK' || 
        policyData.control_evidence_status === 'OK' ||
        policyData.isChecked === true || 
        policyData.finished === true
      ) {
        passedPolicies++;
      }
    });
    
    const passes = totalPolicies > 0 && passedPolicies === totalPolicies;
    const status = `${passedPolicies}/${totalPolicies}`;
    
    return { passes, status };
  } catch (error) {
    console.error('Error checking policies test:', error);
    throw error;
  }
};

/**
 * Checks if all documents in a collection have passed for a specific control
 * @param controlId - The ID of the control to check
 * @param tenantId - The tenant ID
 * @returns Promise with boolean indicating if test passes and status string
 */
export const checkDocumentsTest = async (controlId: string, tenantId: string): Promise<{
  passes: boolean;
  status: string;
}> => {
  try {
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    const tenantRef = doc(db, 'tenants', tenantId);
    const controlsCollectionRef = collection(tenantRef, 'controls');
    const controlDoc = await getDoc(doc(controlsCollectionRef, controlId));

    if (!controlDoc.exists()) {
      throw new Error(`Control with ID ${controlId} not found`);
    }

    const documentsCollectionRef = collection(tenantRef, 'documents');
    
    const documentsQuery = query(documentsCollectionRef, where('control_uid', '==', controlId));
    const documentsSnapshot = await getDocs(documentsQuery);
    
    let totalDocuments = 0;
    let passedDocuments = 0;
    
    documentsSnapshot.forEach((doc) => {
      totalDocuments++;
      const documentData = doc.data();
      
      if (
        documentData.status === 'Approved' || 
        documentData.isApproved === true
      ) {
        passedDocuments++;
      }
    });
    
    const passes = totalDocuments > 0 && passedDocuments === totalDocuments;
    const status = `${passedDocuments}/${totalDocuments}`;
    
    return { passes, status };
  } catch (error) {
    console.error('Error checking documents test:', error);
    throw error;
  }
};

/**
 * Checks if all policies for a GDPR control have "Approved" status
 * @param controlId - The ID of the control to check
 * @param tenantId - The tenant ID
 * @returns Promise with boolean indicating if test passes and status string
 */
export const checkGDPRPoliciesTest = async (controlId: string, tenantId: string): Promise<{ passes: boolean; status: string; }> => {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      const tenantRef = doc(db, 'tenants', tenantId);
      const controlsCollectionRef = collection(tenantRef, 'controls');
      const controlDoc = await getDoc(doc(controlsCollectionRef, controlId));

      if (!controlDoc.exists()) {
        throw new Error(`Control with ID ${controlId} not found`);
      }

      const activeFrameworks = await getActiveFrameworks(tenantId);

      if (!activeFrameworks.includes('GDPR')) {
        return { passes: false, status: '0/0' };
      }

      const policiesCollectionRef = collection(tenantRef, 'policies');
      const policiesSnapshot = await getDocs(policiesCollectionRef);

      let filteredPolicies: DocumentData[] = [];

      policiesSnapshot.forEach((doc) => {
        const policyData = doc.data();

        if (!policyData.frameworks || !Array.isArray(policyData.frameworks) || policyData.frameworks.length === 0) {
          return;
        }

        const matchesFramework = policyData.frameworks.some(frameworkItem =>
          activeFrameworks.some(activeFramework =>
            frameworkItem.startsWith(activeFramework) &&
            !frameworkItem.startsWith('ISO 27001:2013')
          )
        );

        if (matchesFramework) {
          filteredPolicies.push(policyData);
        }
      });

      let totalPolicies = filteredPolicies.length;
      let passedPolicies = 0;

      filteredPolicies.forEach((policyData) => {
        if (policyData.status === 'Approved') {
          passedPolicies++;
        }
      });

      const passes = totalPolicies > 0 && passedPolicies === totalPolicies;
      const status = `${passedPolicies}/${totalPolicies}`;

      return { passes, status };
    } catch (error) {
      console.error('Error checking GDPR policies test:', error);
      throw error;
    }
  };
  

/**
 * Combines test results from multiple tests
 * @param results - Array of test results
 * @returns Combined test result
 */
export const combineTestResults = (results: { passes: boolean; status: string }[]): {
  passes: boolean;
  status: string;
} => {
  if (results.length === 0) {
    return { passes: false, status: '0/0' };
  }
  
  let totalPassed = 0;
  let totalItems = 0;
  
  results.forEach(result => {
    const [passed, total] = result.status.split('/').map(Number);
    totalPassed += passed;
    totalItems += total;
  });
  
  return {
    passes: totalItems > 0 && totalPassed === totalItems,
    status: `${totalPassed}/${totalItems}`
  };
};

/**
 * Runs tests for specific GDPR controls and updates their status
 * @param tenantId - The tenant ID
 * @param controlIdParam - Optional control ID parameter (if not provided, both controls will be processed)
 * @returns Promise with combined test result
 */
export const runControlTests = async (tenantId: string, controlIdParam?: string): Promise<{
  passes: boolean;
  status: string;
}> => {
  try {
    const activeFrameworks = await getActiveFrameworks(tenantId);

    const controlIds = ['ZAFvFPIF6tmq4NjQpuXC', 'Ojzr7C4VtnGDVbFtPPYs'];

    const controlsToProcess = controlIdParam && controlIds.includes(controlIdParam)
      ? [controlIdParam]
      : controlIds;

    if (!activeFrameworks.includes('GDPR')) {
      return { passes: false, status: '0/0' };
    }

    let allResults: { passes: boolean; status: string }[] = [];

    for (const controlId of controlsToProcess) {
      const tenantRef = doc(db, 'tenants', tenantId);
      const controlsCollectionRef = collection(tenantRef, 'controls');
      const controlDoc = await getDoc(doc(controlsCollectionRef, controlId));

      if (!controlDoc.exists()) {
        continue;
      }

      const controlRef = doc(controlsCollectionRef, controlId);

      const gdprResult = await checkGDPRPoliciesTest(controlId, tenantId);

      if (gdprResult.passes) {
        await updateDoc(controlRef, {
          control_evidence_status: 'OK'
        });
      }

      allResults.push(gdprResult);
    }

    if (allResults.length > 1) {
      return combineTestResults(allResults);
    } else if (allResults.length === 1) {
      return allResults[0];
    } else {
      return { passes: false, status: '0/0' };
    }
  } catch (error) {
    console.error('Error running control tests:', error);
    throw error;
  }
};

/**
 * Runs tests for all controls in a tenant
 * @param tenantId - The tenant ID
 * @returns Promise with array of test results for each control
 */
export const runAllControlTests = async (tenantId: string): Promise<{
  controlId: string;
  controlTitle: string;
  status: string;
  passes: boolean;
}[]> => {
  try {
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    const tenantRef = doc(db, 'tenants', tenantId);
    const controlsCollectionRef = collection(tenantRef, 'controls');
    const controlsSnapshot = await getDocs(controlsCollectionRef);

    const results: {
      controlId: string;
      controlTitle: string;
      status: string;
      passes: boolean;
    }[] = [];

    for (const controlDoc of controlsSnapshot.docs) {
      const controlData = controlDoc.data();
      const controlId = controlDoc.id;

      if (controlData.control_evidence_status === 'Deactivated') {
        continue;
      }

      let testResult;

      const gdprControlIds = ['xZAFvFPIF6tmq4NjQpuXC', 'Ojzr7C4VtnGDVbFtPPYs'];

      if (gdprControlIds.includes(controlId)) {
        testResult = await runControlTests(tenantId, controlId);
      } else {
        const policiesResult = await checkPoliciesTest(controlId, tenantId);
        const documentsResult = await checkDocumentsTest(controlId, tenantId);
        testResult = combineTestResults([policiesResult, documentsResult]);

        const controlRef = doc(collection(doc(db, 'tenants', tenantId), 'controls'), controlId);

        await updateDoc(controlRef, {
          control_test: testResult.status
        });

        if (testResult.passes) {
          await updateDoc(controlRef, {
            control_evidence_status: 'OK'
          });
        }
      }

      results.push({
        controlId,
        controlTitle: controlData.control_title || 'Untitled Control',
        status: testResult.status,
        passes: testResult.passes
      });
    }

    return results;
  } catch (error) {
    console.error('Error running all control tests:', error);
    throw error;
  }
};

/**
 * Checks if a specific test passes for a control
 * @param controlId - The ID of the control to check
 * @param testType - The type of test to check (e.g., 'policies', 'documents', 'gdpr_policies')
 * @param tenantId - The tenant ID
 * @returns Promise with test result
 */
export const checkSpecificTest = async (
  controlId: string, 
  testType: 'policies' | 'documents' | 'gdpr_policies', 
  tenantId: string
): Promise<{
  passes: boolean;
  status: string;
}> => {
  try {
    switch (testType) {
      case 'policies':
        return await checkPoliciesTest(controlId, tenantId);
      case 'documents':
        return await checkDocumentsTest(controlId, tenantId);
      case 'gdpr_policies':
        return await checkGDPRPoliciesTest(controlId, tenantId);
      default:
        throw new Error(`Unknown test type: ${testType}`);
    }
  } catch (error) {
    console.error(`Error checking ${testType} test:`, error);
    throw error;
  }
};